import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { normalizeRoomId } from '../services/socket';
import ResumeSessionCard from '../components/ResumeSessionCard';

const Home: React.FC = () => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isDevMode = process.env.NODE_ENV === 'development';
  const devDefaultUsername = 'DevHost';
  const devBotCount = 3;

  const handleCreateRoom = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }
    
    // Generate a random room ID
    const newRoomId = normalizeRoomId(Math.random().toString(36).substring(2, 8));
    
    // Navigate to the room
    navigate(`/room/${newRoomId}`, { state: { username, createRoom: true } });
  };

  const handleJoinRedirect = () => {
    navigate('/join');
  };

  const handleCreateDevLobby = () => {
    const newRoomId = normalizeRoomId(`dev${Math.random().toString(36).substring(2, 8)}`);
    navigate(`/room/${newRoomId}`, {
      state: {
        username: devDefaultUsername,
        createRoom: true,
        devBots: devBotCount
      }
    });
  };

  return (
    <div className="landing">
      <motion.section
        className="hero"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="eyebrow">Multiplayer music trivia</span>
        <h1 className="hero-title">
          Guess the song.
          <br />
          <span className="text-gradient">Beat your friends.</span>
        </h1>
        <p className="hero-subtitle">
          Everyone picks a track around a theme, then you race in real time to name each song before
          the clock runs out.
        </p>
      </motion.section>

      <ResumeSessionCard />

      <motion.div
        className="panel auth-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
      >
        <form onSubmit={handleCreateRoom} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="username">
              Your name
            </label>
            <input
              className="input"
              type="text"
              id="username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError('');
              }}
              placeholder="e.g. DJ Nova"
              autoComplete="nickname"
              autoFocus
            />
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="error-text"
              role="alert"
            >
              {error}
            </motion.p>
          )}

          <div className="cta-row">
            <button className="btn btn-primary btn-lg" type="submit">
              Create a room
            </button>
            <button className="btn btn-secondary btn-lg" type="button" onClick={handleJoinRedirect}>
              Join with a code
            </button>
          </div>

          {isDevMode && (
            <button className="btn btn-ghost btn-sm dev-btn" type="button" onClick={handleCreateDevLobby}>
              Create dev lobby
            </button>
          )}
        </form>
      </motion.div>

      <motion.section
        className="features"
        aria-label="How it works"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="feature">
          <span className="feature-step">1</span>
          <h3>Set a theme</h3>
          <p>The host picks a theme, like 90s hits or movie soundtracks. Everyone submits a song that fits.</p>
        </div>
        <div className="feature">
          <span className="feature-step">2</span>
          <h3>Race to guess</h3>
          <p>Each round plays a 60-second clip. Type the title as fast as you can. Letters reveal over time.</p>
        </div>
        <div className="feature">
          <span className="feature-step">3</span>
          <h3>Score live</h3>
          <p>Faster guesses earn more. Your own pick earns you a bonus for every player who gets it.</p>
        </div>
      </motion.section>

      <section className="about" aria-label="About Songle">
        <h2>Multiplayer song guessing game</h2>
        <p>
          Songle is a free online multiplayer music guessing game. Listen to short audio clips, then race to type the
          song title before the timer runs out. Each game is built around a theme, so players search and
          submit songs that match the theme before the guessing rounds begin. Create a room, share an invite link, and play
          together in real time.
        </p>
        <p>
          If you like <strong>skribbl.io</strong>, Songle has a similar party-game guessing style:
          everyone guesses at once, points update live, and the answer is revealed each round—except here you’re guessing
          songs instead of drawings.
        </p>
      </section>
    </div>
  );
};

export default Home;
